import { useFormik } from 'formik';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Button from '../../bootstrap/Button';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import Checks, { ChecksGroup } from '../../bootstrap/forms/Checks';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';
import Label from '../../bootstrap/forms/Label';
import { DefaultContext } from '../../../contexts/default';
import StoreCategoryDB from '../../../database/wrappers/storeCategory';
import UserHistory from '../../../database/wrappers/userHistory';
import PreAlert from '../../../helpers/utils/preAlert';
import { WEEK_DAYS, createDefaultBlockedSchedule, normalizeBlockedSchedules } from '../../../helpers/utils/categorySchedule';

const validate = (values) => {
  const errors = {};

  if (values.scheduleEnabled) {
    const schedules = normalizeBlockedSchedules(values.blockedSchedules);

    if (schedules.some((schedule) => !schedule.start || !schedule.end)) {
      errors.blockedSchedules = 'Informe quando a categoria deve ser ocultada e voltar a aparecer';
    }

    if (schedules.some((schedule) => !schedule.days.length)) {
      errors.blockedSchedules = 'Selecione ao menos um dia da semana';
    }
  }

  return errors;
};

const ModalStoreCategorySchedule = ({ open, setIsOpen, setIsClose, categorySelected }) => {
  const { establishment, store, onShowAlert, user } = useContext(DefaultContext);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      scheduleEnabled: false,
      blockedSchedules: [createDefaultBlockedSchedule()],
    },
    validate,
    onSubmit: (values) => {
      if (!categorySelected || !establishment || !store) return;

      const data = {
        scheduleEnabled: Boolean(values.scheduleEnabled),
        blockedSchedules: values.scheduleEnabled ? normalizeBlockedSchedules(values.blockedSchedules) : [],
      };

      setIsLoading(true);
      const storeCategoryDB = new StoreCategoryDB(establishment.id, store.id);
      const save = categorySelected.storeCategoryExists
        ? storeCategoryDB.update(categorySelected.id, data)
        : storeCategoryDB.define(categorySelected.id, data);

      save
        .then(() => {
          onShowAlert(PreAlert.success('Horários de exibição atualizados com sucesso'));
          new UserHistory(establishment.id, user, 'a Categoria', store.name)
            .update(categorySelected.id, { data, selected: categorySelected })
            .finally(() => {});
          setIsClose();
        })
        .catch(() => onShowAlert(PreAlert.error('Falha ao atualizar os horários de exibição')))
        .finally(() => setIsLoading(false));
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      return;
    }

    formik.setValues({
      scheduleEnabled: Boolean(categorySelected?.scheduleEnabled),
      blockedSchedules: normalizeBlockedSchedules(categorySelected?.blockedSchedules),
    });
  }, [open, categorySelected]);

  const handleChangeScheduleField = useCallback((index, field, value) => {
    const schedules = normalizeBlockedSchedules(formik.values.blockedSchedules);

    formik.setValues({
      ...formik.values,
      blockedSchedules: schedules.map((schedule, scheduleIndex) => (
        scheduleIndex === index ? { ...schedule, [field]: value } : schedule
      )),
    });
  }, [formik.values]);

  const handleToggleScheduleDay = useCallback((index, day) => {
    const schedules = normalizeBlockedSchedules(formik.values.blockedSchedules);
    const schedule = schedules[index] || createDefaultBlockedSchedule();
    const days = schedule.days.includes(day)
      ? schedule.days.filter((item) => item !== day)
      : [...schedule.days, day].sort((a, b) => a - b);

    formik.setValues({
      ...formik.values,
      blockedSchedules: schedules.map((item, scheduleIndex) => (
        scheduleIndex === index ? { ...item, days } : item
      )),
    });
  }, [formik.values]);

  const handleAddSchedule = useCallback(() => {
    formik.setValues({
      ...formik.values,
      blockedSchedules: [
        ...normalizeBlockedSchedules(formik.values.blockedSchedules),
        createDefaultBlockedSchedule(),
      ],
    });
  }, [formik.values]);

  const handleRemoveSchedule = useCallback((index) => {
    const schedules = normalizeBlockedSchedules(formik.values.blockedSchedules);
    const nextSchedules = schedules.filter((_, scheduleIndex) => scheduleIndex !== index);

    formik.setValues({
      ...formik.values,
      blockedSchedules: nextSchedules.length ? nextSchedules : [createDefaultBlockedSchedule()],
    });
  }, [formik.values]);

  const blockedSchedules = useMemo(
    () => normalizeBlockedSchedules(formik.values.blockedSchedules),
    [formik.values.blockedSchedules],
  );

  return (
    <Modal
      id='modal-store-category-schedule'
      titleId='Configurar horários de exibição'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop
      isCentered
      size='lg'
      isAnimation
      onSubmit={formik.handleSubmit}
    >
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id='store-category-schedule-title'>
          {categorySelected ? `Exibição de ${categorySelected.name}` : 'Horários de exibição'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className='p-5'>
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className='row'>
            <FormGroup className='col-md-12 mb-3'>
              <ChecksGroup
                isValid={formik.isValid}
                isTouched={formik.touched.scheduleEnabled}
                invalidFeedback={formik.errors.scheduleEnabled}
              >
                <Checks
                  type='switch'
                  id='scheduleEnabled'
                  label='Ocultar esta categoria em horários específicos'
                  name='scheduleEnabled'
                  onChange={formik.handleChange}
                  checked={formik.values.scheduleEnabled}
                />
              </ChecksGroup>
            </FormGroup>

            {formik.values.scheduleEnabled ? (
              <>
                {blockedSchedules.map((blockedSchedule, index) => (
                  <div className='row border-top pt-3 mt-3' key={`blockedSchedule${index}`}>
                    <div className='col-md-12 d-flex justify-content-between align-items-center mb-3'>
                      <strong>Período oculto {index + 1}</strong>
                      {blockedSchedules.length > 1 ? (
                        <Button
                          type='button'
                          color='danger'
                          icon='Trash'
                          rounded={1}
                          size='sm'
                          onClick={() => handleRemoveSchedule(index)}
                        >
                          Remover
                        </Button>
                      ) : null}
                    </div>

                    <FormGroup id={`blockedScheduleStart${index}`} label='Ocultar a partir de' className='col-md-3 mb-3'>
                      <Input
                        type='time'
                        value={blockedSchedule.start}
                        onChange={(event) => handleChangeScheduleField(index, 'start', event.target.value)}
                        isValid={formik.isValid}
                        isTouched={formik.touched.blockedSchedules}
                        invalidFeedback={formik.errors.blockedSchedules}
                      />
                    </FormGroup>

                    <FormGroup id={`blockedScheduleEnd${index}`} label='Voltar a exibir às' className='col-md-3 mb-3'>
                      <Input
                        type='time'
                        value={blockedSchedule.end}
                        onChange={(event) => handleChangeScheduleField(index, 'end', event.target.value)}
                        isValid={formik.isValid}
                        isTouched={formik.touched.blockedSchedules}
                        invalidFeedback={formik.errors.blockedSchedules}
                      />
                    </FormGroup>

                    <FormGroup className='col-md-6 mb-3'>
                      <Label>Repetir nos dias</Label>
                      <ChecksGroup
                        isInline
                        isValid={formik.isValid}
                        isTouched={formik.touched.blockedSchedules}
                        invalidFeedback={formik.errors.blockedSchedules}
                      >
                        {WEEK_DAYS.map((day) => (
                          <Checks
                            key={day.value}
                            type='checkbox'
                            id={`blockedScheduleDay${index}${day.value}`}
                            label={day.label}
                            value={day.value}
                            onChange={() => handleToggleScheduleDay(index, day.value)}
                            checked={blockedSchedule.days.includes(day.value)}
                            isInline
                          />
                        ))}
                      </ChecksGroup>
                      {index === blockedSchedules.length - 1 ? (
                        <small className='text-muted d-block mt-2'>
                          Para ocultar durante o dia inteiro, use 00:00 até 23:59.
                        </small>
                      ) : null}
                    </FormGroup>
                  </div>
                ))}

                <div className='d-flex justify-content-end mt-3'>
                  <Button
                    type='button'
                    color='primary'
                    icon='plus'
                    rounded={1}
                    size='sm'
                    onClick={handleAddSchedule}
                  >
                    Adicionar período
                  </Button>
                </div>
              </>
            ) : null}

            <div className='col-md-12 d-flex justify-content-end mt-4'>
              <Button
                type='submit'
                color='success'
                icon='save'
                isDisable={isLoading}
              >
                Salvar
              </Button>
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ModalStoreCategorySchedule;
